import { Head, Link, router } from "@inertiajs/react"
import {
  Box, Button, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography
} from "@mui/material"
import AdminLayout from "../../../layouts/AdminLayout"
import AudioPlayButton from "../../../components/AudioPlayButton"
import TranslationsCell from "../../../components/TranslationsCell"

type Translation = { id: number; language: string; text: string; hint: string | null; position: number }

type Audio = { url: string; language: string; ai_model: string; voice: string }

type Word = {
  id: number
  kanji: string
  kana: string
  romaji: string | null
  translations: Translation[]
  audio: Audio | null
}

export default function AdminWordsIndex({ words }: { words: Word[] }) {
  const remove = (word: Word) => {
    if (confirm(`Delete word "${word.kanji || word.kana}"?`)) router.delete(`/admin/words/${word.id}`)
  }

  return (
    <AdminLayout title="Words" maxWidth={false}>
      <Head title="Admin · Words" />
      <Stack direction="row" sx={{ justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5">Words</Typography>
        <Button variant="contained" component={Link} href="/admin/words/new" data-testid="new-word">
          New word
        </Button>
      </Stack>
      {words.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }} data-testid="words-empty">
          <Typography>No words yet.</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table data-testid="words-table">
            <TableHead>
              <TableRow>
                <TableCell>Kanji</TableCell>
                <TableCell>Kana / Romaji</TableCell>
                <TableCell>Translations</TableCell>
                <TableCell>Audio</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {words.map((word) => (
                <TableRow key={word.id} data-testid={`word-row-${word.id}`}>
                  <TableCell><Link href={`/admin/words/${word.id}`}>{word.kanji || "—"}</Link></TableCell>
                  <TableCell>
                    {word.kana}
                    {word.romaji && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {word.romaji}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell><TranslationsCell translations={word.translations} /></TableCell>
                  <TableCell>
                    {word.audio
                      ? <AudioPlayButton src={word.audio.url} testId={`word-audio-${word.id}`} />
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
                      <Button size="small" component={Link} href={`/admin/words/${word.id}/edit`}>Edit</Button>
                      <Button size="small" color="error" onClick={() => remove(word)}
                              data-testid={`delete-${word.id}`}>Delete</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </AdminLayout>
  )
}
